import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Pause, Play, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

type Dir = "UP" | "DOWN" | "LEFT" | "RIGHT";
type Cell = { x: number; y: number };

const COLS = 14;
const ROWS = 18;
const TICK_MS = 140;

const randCell = (exclude: Cell[]): Cell => {
  while (true) {
    const c = { x: Math.floor(Math.random() * COLS), y: Math.floor(Math.random() * ROWS) };
    if (!exclude.some((e) => e.x === c.x && e.y === c.y)) return c;
  }
};

const opposite: Record<Dir, Dir> = { UP: "DOWN", DOWN: "UP", LEFT: "RIGHT", RIGHT: "LEFT" };

interface Props {
  /** Optional caption shown above the board (e.g. current loading stage). */
  caption?: string;
}

export const SnakeGame = ({ caption }: Props) => {
  const [snake, setSnake] = useState<Cell[]>([
    { x: 6, y: 9 },
    { x: 5, y: 9 },
    { x: 4, y: 9 },
  ]);
  const [dir, setDir] = useState<Dir>("RIGHT");
  const [food, setFood] = useState<Cell>({ x: 10, y: 9 });
  const [score, setScore] = useState(0);
  const [best, setBest] = useState<number>(() => {
    if (typeof window === "undefined") return 0;
    return Number(localStorage.getItem("tryfit_snake_best") ?? 0);
  });
  const [paused, setPaused] = useState(false);
  const [dead, setDead] = useState(false);

  const dirRef = useRef(dir);
  dirRef.current = dir;
  const queuedRef = useRef<Dir | null>(null);

  const reset = useCallback(() => {
    const start: Cell[] = [
      { x: 6, y: 9 },
      { x: 5, y: 9 },
      { x: 4, y: 9 },
    ];
    setSnake(start);
    setDir("RIGHT");
    setFood(randCell(start));
    setScore(0);
    setDead(false);
    setPaused(false);
    queuedRef.current = null;
  }, []);

  const turn = useCallback((next: Dir) => {
    if (next === opposite[dirRef.current] || next === dirRef.current) return;
    queuedRef.current = next;
  }, []);

  // Keyboard controls
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const map: Record<string, Dir> = {
        ArrowUp: "UP", ArrowDown: "DOWN", ArrowLeft: "LEFT", ArrowRight: "RIGHT",
        w: "UP", s: "DOWN", a: "LEFT", d: "RIGHT",
      };
      const next = map[e.key];
      if (next) {
        e.preventDefault();
        turn(next);
      } else if (e.key === " ") {
        e.preventDefault();
        setPaused((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [turn]);

  // Game loop
  useEffect(() => {
    if (paused || dead) return;
    const id = setInterval(() => {
      setSnake((prev) => {
        const queued = queuedRef.current;
        if (queued) {
          setDir(queued);
          dirRef.current = queued;
          queuedRef.current = null;
        }
        const head = prev[0];
        const d = dirRef.current;
        const next: Cell = {
          x: head.x + (d === "LEFT" ? -1 : d === "RIGHT" ? 1 : 0),
          y: head.y + (d === "UP" ? -1 : d === "DOWN" ? 1 : 0),
        };
        // wall collision
        if (next.x < 0 || next.y < 0 || next.x >= COLS || next.y >= ROWS) {
          setDead(true);
          return prev;
        }
        // self collision
        if (prev.some((c) => c.x === next.x && c.y === next.y)) {
          setDead(true);
          return prev;
        }
        const ate = next.x === food.x && next.y === food.y;
        const newSnake = ate ? [next, ...prev] : [next, ...prev.slice(0, -1)];
        if (ate) {
          setScore((s) => {
            const ns = s + 1;
            setBest((b) => {
              const nb = Math.max(b, ns);
              if (typeof window !== "undefined") localStorage.setItem("tryfit_snake_best", String(nb));
              return nb;
            });
            return ns;
          });
          setFood(randCell(newSnake));
        }
        return newSnake;
      });
    }, TICK_MS);
    return () => clearInterval(id);
  }, [paused, dead, food.x, food.y]);

  const cells = useMemo(() => {
    const grid: ("snake" | "head" | "food" | null)[][] = Array.from({ length: ROWS }, () =>
      Array(COLS).fill(null),
    );
    snake.forEach((c, i) => {
      if (c.y >= 0 && c.y < ROWS && c.x >= 0 && c.x < COLS) {
        grid[c.y][c.x] = i === 0 ? "head" : "snake";
      }
    });
    grid[food.y][food.x] = "food";
    return grid;
  }, [snake, food]);

  return (
    <div className="flex w-full flex-col items-center gap-3">
      {caption && (
        <p className="text-center text-sm font-medium text-foreground">{caption}</p>
      )}
      <p className="text-xs text-muted-foreground">Play while we cook your look ✨</p>

      <div className="flex w-full items-center justify-between text-xs">
        <span className="rounded-full bg-muted px-2.5 py-1 font-semibold">Score: {score}</span>
        <span className="rounded-full bg-muted px-2.5 py-1 font-semibold">Best: {best}</span>
      </div>

      <div
        className="relative w-full max-w-[280px] overflow-hidden rounded-xl border border-border bg-muted/40 p-1.5 shadow-soft"
        style={{ aspectRatio: `${COLS}/${ROWS}` }}
      >
        <div
          className="grid h-full w-full gap-[2px]"
          style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)`, gridTemplateRows: `repeat(${ROWS}, 1fr)` }}
        >
          {cells.flat().map((kind, i) => (
            <div
              key={i}
              className={
                kind === "head"
                  ? "rounded-[3px] bg-primary shadow-glow"
                  : kind === "snake"
                    ? "rounded-[3px] bg-primary/70"
                    : kind === "food"
                      ? "rounded-full bg-accent animate-pulse"
                      : "rounded-[2px] bg-background/40"
              }
            />
          ))}
        </div>

        {(paused || dead) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/85 backdrop-blur-sm">
            <p className="text-base font-bold">{dead ? "Game over" : "Paused"}</p>
            {dead && <p className="text-xs text-muted-foreground">Score {score}</p>}
            <Button size="sm" onClick={reset} className="mt-1">
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              {dead ? "Play again" : "Restart"}
            </Button>
          </div>
        )}
      </div>

      {/* On-screen D-pad */}
      <div className="grid grid-cols-3 gap-1.5">
        <div />
        <Button
          variant="outline"
          size="icon"
          className="h-10 w-10"
          onClick={() => turn("UP")}
          aria-label="Up"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-10 w-10"
          onClick={() => setPaused((p) => !p)}
          aria-label={paused ? "Resume" : "Pause"}
        >
          {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-10 w-10"
          onClick={() => turn("LEFT")}
          aria-label="Left"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-10 w-10"
          onClick={() => turn("DOWN")}
          aria-label="Down"
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-10 w-10"
          onClick={() => turn("RIGHT")}
          aria-label="Right"
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
